/**
 * Text a Technical Delegate TYPES into a legal record must be text a reader can see, drawn in the
 * order it was typed.
 *
 * Authority: migration 066 and the Technical Delegate's Decision #6 = A (19 Sep 2026) - a
 * corrected hearing service keeps its reason and its old time, and shows them to whoever opens
 * the case file, on an appeal among other places; the review rounds of that migration (fix round
 * 1: security L2, backend L6; fix round 2: security R1, backend N1; the final round: backend
 * G3, security N1 and N2).
 *
 * ----------------------------------------------------------------------------
 * FOUR FAULTS, ONE RULE, BOTH SIDES OF THE WIRE
 * ----------------------------------------------------------------------------
 *
 * Service capture takes two typed statements: who service was effected on, and - for a
 * correction or a withdrawal - why. Migration 033's refusals test them with Postgres's `btrim`,
 * which strips the ASCII space and nothing else, so a statement of one tab, one Hangul filler or
 * one zero-width space passed as a statement and was kept for good; a NUL passed the fixtures twin
 * and broke the Postgres one with a 500; and nothing stopped a character that reorders, hides or
 * draws over the words around it.
 *
 *   * A BIDI CONTROL - anything with Unicode's own Bidi_Control property: the arabic letter mark
 *     (U+061C), the left-to-right and right-to-left marks (U+200E, U+200F), the embeddings and
 *     overrides (U+202A-U+202E) and the isolates (U+2066-U+2069). Refused, naming the character:
 *     the words of a record must be drawn in the order they were typed, and "14:05" with a mark on
 *     either side of the colon is not the time it looks like.
 *   * A CONTROL CHARACTER other than TAB, LF and CR - the rest of \p{Cc}, C0 and C1 both. Refused,
 *     naming the character: it is not text, the database cannot store U+0000 at all, and a record
 *     should not carry a terminal escape.
 *   * A BROKEN CHARACTER - a lone UTF-16 surrogate (\p{Cs}): half of a character that arrived
 *     without its other half, which JSON can carry as an escape. Refused, naming it: the fixtures
 *     twin would keep it as it came and Postgres would store a replacement character, so the two
 *     would hold different records of one statement. A whole character written as a surrogate
 *     PAIR - an emoji, say - is one code point and is not touched.
 *   * EIGHT OR MORE COMBINING MARKS IN A ROW (\p{M}{8,}). Refused, naming the first: marks stacked
 *     that deep draw over the lines around them. The limit sits above what the writing systems of
 *     this tournament's officials need in practice. The densest is Myanmar, which can put up to
 *     three medials, two vowel signs, an asat and a tone mark on one consonant - seven marks; even
 *     a plain syllable such as KA with medial RA, medial WA, vowel E, AA and ASAT carries five,
 *     which the first limit of five refused. Arabic with full vowel marks, Devanagari, Thai and
 *     decomposed (NFD) Latin carry three or fewer in ordinary text. Eight is the first count none
 *     of them reaches, and it is still far below the dozens a stacking attack needs to draw over a
 *     neighbouring line.
 *   * NO VISIBLE TEXT - every character is white space, a default-ignorable code point, a control,
 *     a format character (\p{Cf}: a soft hyphen, the interlinear annotation marks U+FFF9-U+FFFB,
 *     the Arabic number sign on its own), a non-spacing or enclosing mark, or one of six characters
 *     that are blank by design (the braille blank, the four Hangul fillers and the Mongolian vowel
 *     separator). Treated as the statement not having been made at all, so a correction or a
 *     service meets migration 033's own refusal (HT203, HT205) exactly as an empty field does. A
 *     format character beside visible text is left alone - U+0600 before digits is how a number is
 *     written, and an emoji with its variation selector is an emoji.
 *
 * The first four are refused wherever the character is and whatever surrounds it, and the first
 * such character in the text is the one named. The route (`apps/api/src/routes/hearing-documents.ts`)
 * applies this before either repository twin sees the statement, which is what makes the two twins
 * behave identically through it, and the service form
 * (`apps/web/src/features/hearings/ServiceCapture.tsx`) applies the same rule a moment earlier, so
 * it refuses nothing the route would admit. 033's own refusals are NOT changed: their conditions,
 * SQLSTATEs, messages and hints stay byte for byte what they were.
 *
 * The classes are Unicode properties and code points, never literal characters or escapes, so the
 * source reads the same in every editor and can be compared with the findings range for range.
 */
/** Unicode's Bidi_Control property: U+061C, U+200E, U+200F, U+202A-U+202E, U+2066-U+2069. */
const BIDI_CONTROL = /\p{Bidi_Control}/u;
/** C0 and C1 controls. Of these, TAB, LF and CR are text; the rest are refused. */
const CONTROL = /\p{Cc}/u;
const TEXT_CONTROLS = new Set([0x09, 0x0a, 0x0d]);
/** Half of a character: a lone UTF-16 surrogate. A surrogate PAIR is one code point, not this. */
const BROKEN = /\p{Cs}/u;
/** Eight or more combining marks in a row, spacing or not - see the header for why eight. */
const COMBINING_RUN = /\p{M}{8,}/u;
/**
 * What draws nothing: white space, default-ignorables, controls, format characters, non-spacing and
 * enclosing marks.
 */
const DRAWS_NOTHING = /[\s\p{Default_Ignorable_Code_Point}\p{Cc}\p{Cf}\p{Mn}\p{Me}]/u;
/**
 * Blank by design, whatever their properties say: U+2800 BRAILLE PATTERN BLANK, U+115F and U+1160
 * the Hangul choseong and jungseong fillers, U+3164 HANGUL FILLER, U+FFA0 HALFWIDTH HANGUL FILLER,
 * and U+180E MONGOLIAN VOWEL SEPARATOR.
 */
const BLANK_BY_DESIGN = new Set([0x2800, 0x115f, 0x1160, 0x3164, 0xffa0, 0x180e]);
/** `U+202E`, `U+E0020` - how a refusal names the character it found. */
function codePointLabel(codePoint) {
    return `U+${codePoint.toString(16).toUpperCase().padStart(4, "0")}`;
}
/**
 * Judge one typed statement.
 *
 * In order: the first bidi control, forbidden control character or broken character in the text,
 * wherever it is; then a run of eight or more combining marks; then whether anything visible is
 * there at all. So
 * a statement consisting of nothing but a right-to-left mark is refused for the mark, not read as
 * empty - the rule never depends on what surrounds the character.
 */
export function recordTextFault(value) {
    let visible = false;
    for (const character of value) {
        const codePoint = character.codePointAt(0) ?? 0;
        if (BIDI_CONTROL.test(character)) {
            return { kind: "bidi_control", codePoint: codePointLabel(codePoint) };
        }
        if (CONTROL.test(character) && !TEXT_CONTROLS.has(codePoint)) {
            return { kind: "control_character", codePoint: codePointLabel(codePoint) };
        }
        if (BROKEN.test(character)) {
            return { kind: "broken_character", codePoint: codePointLabel(codePoint) };
        }
        if (!visible && !DRAWS_NOTHING.test(character) && !BLANK_BY_DESIGN.has(codePoint)) {
            visible = true;
        }
    }
    const run = COMBINING_RUN.exec(value);
    if (run !== null) {
        return {
            kind: "combining_run",
            codePoint: codePointLabel(run[0].codePointAt(0) ?? 0),
            length: [...run[0]].length,
        };
    }
    return visible ? null : { kind: "no_visible_text" };
}
/**
 * The refusal of a statement carrying a bidi control, in the words the route answers with and the
 * service form says first. `subject` begins the sentence: "The reason", "Who it was served on".
 */
export function bidiControlRefusal(subject, codePoint) {
    return (`${subject} contains a direction-control character (${codePoint}), which can make the words ` +
        "display in a different order from the one they were typed in. Type it again without that " +
        "character.");
}
/** The refusal of a statement carrying a control character other than TAB, LF or CR. */
export function controlCharacterRefusal(subject, codePoint) {
    return (`${subject} contains a control character (${codePoint}), which is not text and cannot be kept ` +
        "in the record. Type it again without that character.");
}
/** The refusal of a statement carrying half of a character - a lone surrogate. */
export function brokenCharacterRefusal(subject, codePoint) {
    return (`${subject} contains a broken character (${codePoint}) - half of a character that arrived ` +
        "without its other half - which cannot be kept in the record. Type it again.");
}
/** The refusal of a statement carrying eight or more combining marks in a row. */
export function combiningRunRefusal(subject, codePoint, length) {
    return (`${subject} contains ${String(length)} combining marks in a row, starting ${codePoint}, which ` +
        "can draw over the words around them. Type it again with no more than seven marks on any one " +
        "letter.");
}
/** The words for any fault that is refused outright - one switch, so the route and the form agree. */
export function recordTextRefusal(subject, fault) {
    switch (fault.kind) {
        case "bidi_control":
            return bidiControlRefusal(subject, fault.codePoint);
        case "control_character":
            return controlCharacterRefusal(subject, fault.codePoint);
        case "broken_character":
            return brokenCharacterRefusal(subject, fault.codePoint);
        case "combining_run":
            return combiningRunRefusal(subject, fault.codePoint, fault.length);
    }
}
//# sourceMappingURL=record-text.js.map